<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type ProducerLinkStatus = 'active' | 'pending'
type LgpdRequestStatus = 'report' | 'edit' | 'delete'

const props = withDefaults(
  defineProps<{
    status: ProducerLinkStatus
    requests?: LgpdRequestStatus[]
    isMobile?: boolean
  }>(),
  {
    requests: () => [],
    isMobile: false,
  }
)

const { t } = useI18n()

const statusMeta = computed(() => {
  if (props.status === 'pending') {
    return {
      text: t('eveLink.favoriteProducersList.pending'),
      className: 'status-line__status status-line__status--warn',
      icon: 'mdi-timer-sand',
    }
  }

  return {
    text: t('eveLink.favoriteProducersList.active'),
    className: 'status-line__status status-line__status--success',
    icon: 'mdi-check-circle',
  }
})

const requestItems = computed(() => {
  const reqs = props.requests ?? []

  return reqs.map((req) => {
    if (req === 'report') {
      return {
        key: 'report',
        text: t('eveLink.favoriteProducersList.report'),
        className: 'status-line__request status-line__request--muted',
      }
    }

    if (req === 'edit') {
      return {
        key: 'edit',
        text: t('eveLink.favoriteProducersList.edit'),
        className: 'status-line__request status-line__request--muted',
      }
    }

    return {
      key: 'delete',
      text: t('eveLink.favoriteProducersList.delete'),
      className: 'status-line__request status-line__request--error',
    }
  })
})
</script>

<template>
  <div class="status-line" :class="{ 'status-line--mobile': isMobile }">
    <span :class="statusMeta.className">
      <v-icon :icon="statusMeta.icon" size="14" class="status-line__icon" />
      <span class="status-line__text">{{ statusMeta.text }}</span>
    </span>

    <template v-for="item in requestItems" :key="item.key">
      <span v-if="!isMobile" class="status-line__sep" aria-hidden="true">
        •
      </span>

      <span :class="item.className">
        <span class="status-line__text">{{ item.text }}</span>
      </span>
    </template>
  </div>
</template>

<style scoped>
.status-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  min-height: 20px;
  padding-bottom: 2px;
  overflow: visible;
}

.status-line--mobile {
  row-gap: 2px;
}

.status-line__status,
.status-line__request {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  overflow: visible;
}

.status-line__text {
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans');
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.048px;
  padding-bottom: 1px;
}

.status-line__icon {
  flex: 0 0 auto;
  align-self: center;
}

.status-line__sep {
  display: inline-flex;
  align-items: center;
  line-height: 18px;
  opacity: 0.55;
  flex: 0 0 auto;
  margin: 0 2px;
}

.status-line__status--success {
  color: var(--standart-theme---EVE-success, #4caf50);
}

.status-line__status--warn {
  color: var(--standart-theme---EVE-warning, #fb8c00);
}

.status-line__request--muted {
  color: var(--opacity-text--secondary, rgba(0, 0, 0, 0.6));
}

.status-line__request--error {
  color: var(--standart-theme---EVE-error, #ff5252);
}
</style>
